import { motion } from "framer-motion";
import { ArrowRight, Phone, Mail, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/animations/ScrollAnimations";

const contactOptions = [
  {
    icon: Calendar,
    title: "Book a Demo",
    description: "See our platform in action with a personalized walkthrough.",
    action: "Schedule Now",
    isPrimary: true,
    link: "https://outlook.office365.com/book/EvGatewayPortalDemos@evgateway.com/"
  },
  {
    icon: Phone,
    title: "Talk to Sales",
    description: "Speak directly with our solutions experts.",
    action: "Contact Us",
    isPrimary: false,
    link: "/contact",
    isInternal: true
  },
  {
    icon: Mail,
    title: "Email Us",
    description: "Send us your requirements and we'll get back to you.",
    action: "solutions@evgateway.com",
    isPrimary: true
  },
];

export function SolutionsCTANew() {
  return (
    <section className="py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-card/30 to-background" />
      <div className="absolute inset-0">
        <motion.div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[200px]"
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Section Dividers */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-5xl mx-auto">
          {/* Main CTA */}
          <ScrollReveal className="text-center mb-20">
            <span className="inline-flex items-center gap-2 glass-card px-5 py-2.5 rounded-full text-sm font-medium text-primary mb-8 text-white">
              Get Started Today
            </span>
            <h2 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
              Ready to <span className="text-gradient">Get Started?</span>
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
              Let's discuss your specific needs and design a charging solution that drives results for your business.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="https://outlook.office365.com/book/EvGatewayPortalDemos@evgateway.com/" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                <Button variant="hero" size="lg" className="group w-full">
                  Schedule Consultation
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Button>
              </a>
              <Button variant="outline" size="lg" className="w-full sm:w-auto border-foreground/30 text-foreground hover:bg-foreground/10 hover:border-foreground/50 rounded-xl">
                Download Solutions Guide
              </Button>
            </div>
          </ScrollReveal>

          {/* Contact Options */}
          <StaggerContainer className="grid md:grid-cols-3 gap-6" staggerDelay={0.1}>
            {contactOptions.map((option, idx) => (
              <StaggerItem key={idx}>
                {option.link ? (
                  option.isInternal ? (
                    <Link to={option.link}>
                      <motion.div
                        whileHover={{ scale: 1.03, y: -6 }}
                        className="glass-card rounded-3xl p-8 text-center group cursor-pointer h-full"
                      >
                        <div className={`w-16 h-16 rounded-2xl ${option.isPrimary ? 'bg-primary/10 group-hover:bg-primary/20' : 'bg-accent/10 group-hover:bg-accent/20'} flex items-center justify-center mx-auto mb-5 transition-colors`}>
                          <option.icon className={`h-8 w-8 ${option.isPrimary ? 'text-primary' : 'text-accent'}`} />
                        </div>
                        <h3 className="font-bold text-xl mb-3 text-white">{option.title}</h3>
                        <p className="text-sm text-white/60 mb-5 leading-relaxed">{option.description}</p>
                        <span className={`${option.isPrimary ? 'text-primary' : 'text-accent'} font-semibold text-sm`}>{option.action}</span>
                      </motion.div>
                    </Link>
                  ) : (
                    <a href={option.link} target="_blank" rel="noopener noreferrer">
                      <motion.div
                        whileHover={{ scale: 1.03, y: -6 }}
                        className="glass-card rounded-3xl p-8 text-center group cursor-pointer h-full"
                      >
                        <div className={`w-16 h-16 rounded-2xl ${option.isPrimary ? 'bg-primary/10 group-hover:bg-primary/20' : 'bg-accent/10 group-hover:bg-accent/20'} flex items-center justify-center mx-auto mb-5 transition-colors`}>
                          <option.icon className={`h-8 w-8 ${option.isPrimary ? 'text-primary' : 'text-accent'}`} />
                        </div>
                        <h3 className="font-bold text-xl mb-3 text-white">{option.title}</h3>
                        <p className="text-sm text-white/60 mb-5 leading-relaxed">{option.description}</p>
                        <span className={`${option.isPrimary ? 'text-primary' : 'text-accent'} font-semibold text-sm`}>{option.action}</span>
                      </motion.div>
                    </a>
                  )
                ) : (
                  <motion.div
                    whileHover={{ scale: 1.03, y: -6 }}
                    className="glass-card rounded-3xl p-8 text-center group cursor-default h-full"
                  >
                    <div className={`w-16 h-16 rounded-2xl ${option.isPrimary ? 'bg-primary/10 group-hover:bg-primary/20' : 'bg-accent/10 group-hover:bg-accent/20'} flex items-center justify-center mx-auto mb-5 transition-colors`}>
                      <option.icon className={`h-8 w-8 ${option.isPrimary ? 'text-primary' : 'text-accent'}`} />
                    </div>
                    <h3 className="font-bold text-xl mb-3 text-white">{option.title}</h3>
                    <p className="text-sm text-white/60 mb-5 leading-relaxed">{option.description}</p>
                    <span className={`${option.isPrimary ? 'text-primary' : 'text-accent'} font-semibold text-sm`}>{option.action}</span>
                  </motion.div>
                )}
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </div>
    </section>
  );
}
